import React from 'react';
import { Copy, Trash2, Plus } from 'lucide-react';
import { LineItem } from './estimateType';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

interface EstimateLineItemsProps {
  lineItems: LineItem[];
  setLineItems: React.Dispatch<React.SetStateAction<LineItem[]>>;
  onTotalsChange: (totals: {
    dollars: number;
    hours: number;
    optionalDollars: number;
    optionalHours: number;
  }) => void;
}

const EstimateLineItems = ({ lineItems, setLineItems, onTotalsChange }: EstimateLineItemsProps) => {
  const updateTotals = (items: LineItem[]) => {
    const totalDollars = items.reduce((sum, item) => sum + item.sub_total_dollar, 0);
    const totalHours = items.reduce((sum, item) => sum + item.duration_hour, 0);
    const optionalItems = items.filter(item => item.is_optional);
    const totalOptionalDollars = optionalItems.reduce((sum, item) => sum + item.sub_total_dollar, 0);
    const totalOptionalHours = optionalItems.reduce((sum, item) => sum + item.duration_hour, 0);

    onTotalsChange({
      dollars: totalDollars,
      hours: totalHours,
      optionalDollars: totalOptionalDollars,
      optionalHours: totalOptionalHours,
    });
  };

  const handleLineItemChange = (id: number, field: string, value: string | number | boolean) => {
    setLineItems(prevItems => {
      const newItems = prevItems.map(item => {
        if (item.id === id) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'unit_price_dollar' || field === 'quantity') {
            const newUnitPrice = field === 'unit_price_dollar' ? Number(value) : item.unit_price_dollar;
            const newQuantity = field === 'quantity' ? Number(value) : item.quantity;
            updatedItem.sub_total_dollar = newUnitPrice * newQuantity;
          }
          return updatedItem;
        }
        return item;
      });
      updateTotals(newItems);
      return newItems;
    });
  };

  const addNewLine = () => {
    const newItem: LineItem = {
      id: Date.now(),
      code: '',
      description: '',
      unit_price_dollar: 0,
      quantity: 0,
      sub_total_dollar: 0,
      cost_type: 'all',
      duration_hour: 0,
      estimate_id: '',
      is_optional: false,
    };
    const newItems = [...lineItems, newItem];
    setLineItems(newItems);
    updateTotals(newItems);
  };

  const copyLine = (id: number) => {
    const itemToCopy = lineItems.find(item => item.id === id);
    if (itemToCopy) {
      const newItems = [...lineItems, { ...itemToCopy, id: Date.now() }];
      setLineItems(newItems);
      updateTotals(newItems);
    }
  };

  const deleteLine = (id: number) => {
    if (lineItems.length > 1) {
      const newItems = lineItems.filter(item => item.id !== id);
      setLineItems(newItems);
      updateTotals(newItems);
    }
  };

  return (
    <div className="space-y-3">
      {lineItems.map((item, index) => {
        const subTotal = (item.unit_price_dollar || 0) * (item.quantity || 0);
        const backgroundColor = index % 2 === 0 ? 'bg-white' : 'bg-gray-50';

        return (
          <div
            key={item.id}
            className={`${backgroundColor} border border-gray-300 rounded-lg overflow-hidden`}
          >
            {/* Header Row - Code + Actions */}
            <div className="flex items-center justify-between gap-4 px-4 py-3 bg-gray-100 border-b border-gray-300">
              <div className="flex-1">
                <Label htmlFor={`code-${item.id}`} className="mb-1">
                  Code <span className="text-destructive">*</span>
                </Label>
                <Input
                  id={`code-${item.id}`}
                  placeholder="Enter Code"
                  value={item.code}
                  onChange={(e) => handleLineItemChange(item.id, 'code', e.target.value)}
                  required
                  className="max-w-md bg-white"
                />
              </div>
              
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => copyLine(item.id)}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                  title="Copy Item"
                >
                  <Copy size={18} className="text-primary" />
                </button>
                {lineItems.length > 1 && (
                  <button
                    type="button"
                    onClick={() => deleteLine(item.id)}
                    className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    title="Delete Item"
                  >
                    <Trash2 size={18} className="text-destructive" />
                  </button>
                )}
              </div>
            </div>

            {/* Description Section */}
            <div className="px-4 py-3 border-b border-gray-200">
              <Label htmlFor={`description-${item.id}`} className="mb-1">
                Description <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id={`description-${item.id}`}
                placeholder="Enter description"
                value={item.description}
                onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)}
                rows={2}
                required
                className="resize-none"
              />
            </div>

            {/* Fields Grid - Desktop: 5 columns, Mobile: responsive */}
            <div className="px-4 py-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {/* Unit Price */}
                <div>
                  <Label htmlFor={`unitPrice-${item.id}`} className="mb-1 text-xs">
                    Unit Price ($) <span className="text-destructive">*</span>
                  </Label>
<Input
  id={`unitPrice-${item.id}`}
  type="number"
  min={1}
  placeholder="Unit Price"
  value={item.unit_price_dollar === 0 ? '' : item.unit_price_dollar}
  onChange={(e) => {
    const inputValue = parseFloat(e.target.value);

    if (isNaN(inputValue) || inputValue <= 0) {
      handleLineItemChange(item.id, 'unit_price_dollar', 0); // store as zero internally
      return;
    }

    const boundedValue = Math.min(inputValue, 100000);
    handleLineItemChange(item.id, 'unit_price_dollar', boundedValue);
  }}
  required
/>

                </div>

                {/* Quantity */}
                <div>
                  <Label htmlFor={`quantity-${item.id}`} className="mb-1 text-xs">
                    Quantity <span className="text-destructive">*</span>
                  </Label>
<Input
  id={`quantity-${item.id}`}
  type="number"
  min={1}
  placeholder="Quantity"
  value={item.quantity === 0 ? '' : item.quantity}
  onChange={(e) => {
    const inputValue = parseFloat(e.target.value);

    if (isNaN(inputValue) || inputValue <= 0) {
      handleLineItemChange(item.id, 'quantity', 0);
      return;
    }

    const boundedValue = Math.min(inputValue, 10000);
    handleLineItemChange(item.id, 'quantity', boundedValue);
  }}
  required
/>

                </div>

                {/* Cost Type */}
                <div>
                  <Label htmlFor={`costType-${item.id}`} className="mb-1 text-xs">
                    Cost Type
                  </Label>
                  <Select
                    id={`costType-${item.id}`}
                    value={item.cost_type}
                    onChange={(e) =>
                      handleLineItemChange(item.id, 'cost_type', e.target.value as 'all' | 'labor' | 'material')
                    }
                  >
                    <option value="all">All</option>
                    <option value="labor">Labor</option>
                    <option value="material">Material</option>
                  </Select>
                </div>

                {/* Optional */}
                <div>
                  <Label htmlFor={`optional-${item.id}`} className="mb-1 text-xs">
                    Optional
                  </Label>
                  <Select
                    id={`optional-${item.id}`}
                    value={item.is_optional ? 'true' : 'false'}
                    onChange={(e) =>
                      handleLineItemChange(item.id, 'is_optional', e.target.value === 'true')
                    }
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </Select>
                </div>

                {/* Sub Total */}
                <div>
                  <Label className="mb-1 text-xs">Subtotal</Label>
                  <div className="h-9 flex items-center justify-end px-3 py-2 bg-blue-50 border-2 border-blue-200 rounded-lg font-bold text-foreground">
                    ${subTotal.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Add New Line Button */}
      <div className="pt-2">
        <Button type="button" size="medium" variant="inverted" onClick={addNewLine}>
          <Plus size={20} />
          Add Itemized Estimate
        </Button>
      </div>
    </div>
  );
};

export default EstimateLineItems;